import SecondaryButton from '@/components/Buttons/SecondaryButton';
import { Heading, Text } from '@/components/Typography';
import useRoutes from '@/hooks/useRoutes';
import {
    AssignmentSummary,
    EvaluationSummary,
    SectionData,
    TopicClassData,
    TopicData,
} from '@/types';
import { Link } from '@inertiajs/react';
import clsx from 'clsx';
import {
    AlertCircle,
    ArrowRight,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    ClipboardList,
    FileText,
    Lock,
    Play,
    X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SectionDrawerProps {
    section: SectionData | null;
    courseSlug: string;
    onClose: () => void;
}

const SectionDrawer: React.FC<SectionDrawerProps> = ({
    section,
    courseSlug,
    onClose,
}) => {
    const { t } = useTranslation();
    const { route } = useRoutes();

    const isOpen = section !== null;

    const [expandedTopicIds, setExpandedTopicIds] = useState<Set<number>>(
        new Set(section?.topics.map((t) => t.id) ?? [])
    );
    const [hoveredTopicId, setHoveredTopicId] = useState<number | null>(null);

    // Expand all topics whenever a different section opens
    useEffect(() => {
        setExpandedTopicIds(new Set(section?.topics.map((t) => t.id) ?? []));
    }, [section?.id]);

    // Lock body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const toggleTopic = (topicId: number) => {
        setExpandedTopicIds((prev) => {
            const next = new Set(prev);
            if (next.has(topicId)) {
                next.delete(topicId);
            } else {
                next.add(topicId);
            }
            return next;
        });
    };

    const buildTopicUrl = (topicId: number) =>
        route('topic-detail', {
            slug: courseSlug,
            topic_id: topicId,
        }).toString();

    const buildEvaluationUrl = (evaluationId: number) =>
        `/evaluations/${evaluationId}/`;

    const sectionUrl = section
        ? route('section-detail', {
              slug: courseSlug,
              section_id: section.id,
          }).toString()
        : '#';

    const isLocked = section ? section.is_locked && !section.can_access : false;

    const totalTopics = section?.topics.length ?? 0;
    const totalClasses = section?.total_classes ?? 0;
    const completedClasses = section?.completed_classes ?? 0;
    const totalAssignments = section?.assignments.length ?? 0;
    const totalEvaluations = section?.evaluations.length ?? 0;

    // Group assignments and evaluations under their topic (they carry topic_id), so each topic
    // renders its own nested items — mirroring the full section page. Items without a topic stay
    // flat at the end.
    const {
        assignmentsByTopic,
        evaluationsByTopic,
        orphanAssignments,
        orphanEvaluations,
    } = useMemo(() => {
        const aByTopic = new Map<number, AssignmentSummary[]>();
        const eByTopic = new Map<number, EvaluationSummary[]>();
        const orphanA: AssignmentSummary[] = [];
        const orphanE: EvaluationSummary[] = [];

        (section?.assignments ?? []).forEach((assignment) => {
            if (!assignment.topic_id) {
                orphanA.push(assignment);
                return;
            }
            const list = aByTopic.get(assignment.topic_id) ?? [];
            list.push(assignment);
            aByTopic.set(assignment.topic_id, list);
        });

        (section?.evaluations ?? []).forEach((evaluation) => {
            if (!evaluation.topic_id) {
                orphanE.push(evaluation);
                return;
            }
            const list = eByTopic.get(evaluation.topic_id) ?? [];
            list.push(evaluation);
            eByTopic.set(evaluation.topic_id, list);
        });

        return {
            assignmentsByTopic: aByTopic,
            evaluationsByTopic: eByTopic,
            orphanAssignments: orphanA,
            orphanEvaluations: orphanE,
        };
    }, [section]);

    const renderClassRow = (cls: TopicClassData) => (
        <Link
            key={`class-${cls.id}`}
            href={`/courses/${courseSlug}/classes/${cls.id}`}
            className="group flex items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:bg-neutral-800 dark:hover:bg-neutral-700"
        >
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 dark:bg-neutral-700">
                {cls.is_completed ? (
                    <CheckCircle size={14} className="text-emerald-500" />
                ) : (
                    <Play
                        size={14}
                        className="group-hover:text-theme-primary-text text-gray-400 transition-colors"
                    />
                )}
            </div>
            <div className="min-w-0 flex-1">
                <Text
                    as="p"
                    size="sm"
                    className="truncate font-medium text-gray-800 dark:text-neutral-200"
                >
                    {cls.title}
                </Text>
                {cls.coming_soon ? (
                    <Text
                        as="p"
                        size="xs"
                        muted
                        className="mt-0.5 dark:text-neutral-400"
                    >
                        {t('course.comingSoon', 'Próximamente')}
                    </Text>
                ) : null}
            </div>
            <ChevronRight
                size={16}
                className="group-hover:text-theme-primary-text flex-shrink-0 text-gray-300 transition-colors"
            />
        </Link>
    );

    const renderAssignmentRow = (assignment: AssignmentSummary) => (
        <Link
            key={`assignment-${assignment.id}`}
            href={`/assignments/${assignment.slug}/`}
            className="group flex items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:bg-neutral-800 dark:hover:bg-neutral-700"
        >
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 dark:bg-neutral-700">
                <ClipboardList
                    size={14}
                    className="group-hover:text-theme-primary-text text-emerald-500 transition-colors"
                />
            </div>
            <div className="min-w-0 flex-1">
                <Text
                    as="p"
                    size="sm"
                    className="truncate font-medium text-gray-800 dark:text-neutral-200"
                >
                    {assignment.title}
                </Text>
                <Text
                    as="p"
                    size="xs"
                    muted
                    className="mt-0.5 dark:text-neutral-400"
                >
                    {t('course.assignment', 'tarea')}
                </Text>
            </div>
            <ChevronRight
                size={16}
                className="group-hover:text-theme-primary-text flex-shrink-0 text-gray-300 transition-colors"
            />
        </Link>
    );

    const renderEvaluationRow = (evaluation: EvaluationSummary) => (
        <Link
            key={`evaluation-${evaluation.id}`}
            href={buildEvaluationUrl(evaluation.id)}
            className="group flex items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:bg-neutral-800 dark:hover:bg-neutral-700"
        >
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 dark:bg-neutral-700">
                <FileText
                    size={14}
                    className="group-hover:text-theme-primary-text text-gray-500 transition-colors"
                />
            </div>
            <div className="min-w-0 flex-1">
                <Text
                    as="p"
                    size="sm"
                    className="truncate font-medium text-gray-800 dark:text-neutral-200"
                >
                    {evaluation.title}
                </Text>
                <Text
                    as="p"
                    size="xs"
                    muted
                    className="mt-0.5 dark:text-neutral-400"
                >
                    1 {t('course.evaluation', 'evaluación')}
                </Text>
            </div>
            <ChevronRight
                size={16}
                className="group-hover:text-theme-primary-text flex-shrink-0 text-gray-300 transition-colors"
            />
        </Link>
    );

    const metaParts: string[] = [];
    if (totalTopics > 0) {
        metaParts.push(`${totalTopics} ${t('course.topics', 'temas')}`);
    }
    if (totalClasses > 0) {
        metaParts.push(
            `${completedClasses}/${totalClasses} ${t('course.classes', 'clases')}`
        );
    }
    if (totalAssignments > 0) {
        metaParts.push(
            `${totalAssignments} ${t('course.assignments', 'tareas')}`
        );
    }
    if (totalEvaluations > 0) {
        metaParts.push(
            `${totalEvaluations} ${t('course.evaluation', 'evaluación')}`
        );
    }

    return (
        <>
            {/* Backdrop */}
            <div
                className={clsx(
                    'fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300',
                    isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
                onClick={onClose}
            />

            {/* Drawer panel */}
            <div
                className={clsx(
                    'fixed inset-y-0 right-0 z-50 flex w-full flex-col overflow-hidden shadow-2xl transition-transform duration-300 ease-in-out sm:max-w-md',
                    'bg-stone-100 dark:bg-neutral-900',
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                )}
            >
                {section && (
                    <>
                        {/* Header */}
                        <div className="flex-shrink-0 border-b border-stone-200 bg-stone-100 p-5 dark:border-neutral-700 dark:bg-neutral-900">
                            <div className="flex items-start gap-3">
                                {/* Section icon */}
                                {section.icon_url && (
                                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white p-2 shadow-sm">
                                        <img
                                            src={section.icon_url}
                                            alt={section.title}
                                            className="h-full w-full object-contain"
                                        />
                                    </div>
                                )}

                                {/* Title + meta */}
                                <div className="min-w-0 flex-1">
                                    <Link
                                        href={sectionUrl}
                                        className="hover:text-theme-primary-text inline-block transition-colors"
                                    >
                                        <Heading
                                            as="h2"
                                            className="text-base tracking-wide uppercase dark:text-white"
                                        >
                                            {section.title}
                                        </Heading>
                                    </Link>
                                    {metaParts.length > 0 && (
                                        <p className="mt-0.5 text-sm text-gray-500 dark:text-neutral-400">
                                            {metaParts.join(' • ')}
                                        </p>
                                    )}
                                </div>

                                {/* Close button */}
                                <button
                                    onClick={onClose}
                                    className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-stone-200 hover:text-gray-600 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-neutral-200"
                                    aria-label="Cerrar"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Completed badge */}
                            {section.is_completed && (
                                <div className="mt-3">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-200 px-3 py-1 text-xs font-medium text-gray-600">
                                        <CheckCircle size={13} />
                                        {t('course.completed', 'Completado')}
                                    </span>
                                </div>
                            )}

                            {!isLocked && (
                                <div className="mt-4">
                                    <SecondaryButton
                                        as="link"
                                        href={sectionUrl}
                                        size="sm"
                                    >
                                        {t(
                                            'course.viewFullSection',
                                            'Ver sección completa'
                                        )}
                                        <ArrowRight size={14} />
                                    </SecondaryButton>
                                </div>
                            )}
                        </div>

                        {/* Scrollable content */}
                        <div className="flex-1 overflow-y-auto p-5">
                            {isLocked ? (
                                <div className="flex flex-col items-center gap-4 py-8 text-center">
                                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-stone-200 dark:bg-neutral-800">
                                        <Lock
                                            size={28}
                                            className="text-gray-400 dark:text-neutral-500"
                                        />
                                    </div>
                                    <div>
                                        <Text
                                            as="p"
                                            className="font-semibold text-gray-700 dark:text-neutral-300"
                                        >
                                            {t(
                                                'section.locked.title',
                                                'Contenido bloqueado'
                                            )}
                                        </Text>
                                        <Text
                                            as="p"
                                            size="sm"
                                            muted
                                            className="mt-1 dark:text-neutral-400"
                                        >
                                            {t(
                                                'section.locked.description',
                                                'Completa los requisitos para desbloquear esta sección.'
                                            )}
                                        </Text>
                                    </div>
                                    {section.unlock_conditions.length > 0 && (
                                        <div className="w-full space-y-2 text-left">
                                            {section.unlock_conditions.map(
                                                (condition, index) => (
                                                    <div
                                                        key={index}
                                                        className={`flex items-center gap-2 rounded-lg border p-3 ${condition.is_met ? 'border-green-200 bg-green-50 dark:border-green-900/50 dark:bg-green-950/30' : 'border-orange-200 bg-orange-50 dark:border-orange-900/50 dark:bg-orange-950/30'}`}
                                                    >
                                                        {condition.is_met ? (
                                                            <CheckCircle
                                                                size={16}
                                                                className="shrink-0 text-green-500"
                                                            />
                                                        ) : (
                                                            <AlertCircle
                                                                size={16}
                                                                className="shrink-0 text-orange-500"
                                                            />
                                                        )}
                                                        <Text
                                                            as="span"
                                                            size="sm"
                                                            className={
                                                                condition.is_met
                                                                    ? 'text-green-800 dark:text-green-300'
                                                                    : 'text-orange-800 dark:text-orange-300'
                                                            }
                                                        >
                                                            {
                                                                condition.description
                                                            }
                                                        </Text>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {/* Collapsible topic rows */}
                                    {section.topics.map(
                                        (topic: TopicData, index: number) => {
                                            const topicAssignments =
                                                assignmentsByTopic.get(
                                                    topic.id
                                                ) ?? [];
                                            const topicEvaluations =
                                                evaluationsByTopic.get(
                                                    topic.id
                                                ) ?? [];
                                            const topicClasses =
                                                topic.classes ?? [];
                                            const isExpanded =
                                                expandedTopicIds.has(topic.id);

                                            return (
                                                <div
                                                    key={topic.id}
                                                    className="space-y-2"
                                                >
                                                    {/* Topic card */}
                                                    <div
                                                        className="overflow-hidden rounded-xl bg-white shadow-sm dark:bg-neutral-800"
                                                        onMouseEnter={() =>
                                                            setHoveredTopicId(
                                                                topic.id
                                                            )
                                                        }
                                                        onMouseLeave={() =>
                                                            setHoveredTopicId(
                                                                null
                                                            )
                                                        }
                                                    >
                                                        <div className="flex items-stretch">
                                                            <button
                                                                type="button"
                                                                aria-expanded={
                                                                    isExpanded
                                                                }
                                                                onClick={() =>
                                                                    toggleTopic(
                                                                        topic.id
                                                                    )
                                                                }
                                                                className="flex min-w-0 flex-1 items-center gap-3 p-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-neutral-700"
                                                            >
                                                                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-xs font-semibold text-gray-600 dark:bg-neutral-700 dark:text-neutral-300">
                                                                    {index + 1}
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <Text
                                                                        as="p"
                                                                        size="sm"
                                                                        className="truncate font-medium text-gray-800 dark:text-neutral-200"
                                                                    >
                                                                        {
                                                                            topic.title
                                                                        }
                                                                    </Text>
                                                                    {topic.classes_count >
                                                                    0 ? (
                                                                        <Text
                                                                            as="p"
                                                                            size="xs"
                                                                            muted
                                                                            className="mt-0.5 dark:text-neutral-400"
                                                                        >
                                                                            {topic.completed_classes ??
                                                                                0}
                                                                            /
                                                                            {
                                                                                topic.classes_count
                                                                            }{' '}
                                                                            {t(
                                                                                'course.classes',
                                                                                'clases'
                                                                            )}
                                                                        </Text>
                                                                    ) : null}
                                                                </div>
                                                                <ChevronDown
                                                                    size={16}
                                                                    className={clsx(
                                                                        'flex-shrink-0 text-gray-300 transition-transform duration-200',
                                                                        isExpanded &&
                                                                            'rotate-180'
                                                                    )}
                                                                />
                                                            </button>

                                                            {/* Slides in from the right — React-controlled so click never triggers it */}
                                                            <div
                                                                className={clsx(
                                                                    'flex flex-shrink-0 items-center overflow-hidden whitespace-nowrap transition-all duration-200',
                                                                    hoveredTopicId ===
                                                                        topic.id
                                                                        ? 'max-w-[220px] opacity-100'
                                                                        : 'pointer-events-none max-w-0 opacity-0'
                                                                )}
                                                            >
                                                                <div className="border-l border-stone-100 py-3 pr-4 pl-3 dark:border-neutral-700">
                                                                    <SecondaryButton
                                                                        as="link"
                                                                        href={buildTopicUrl(
                                                                            topic.id
                                                                        )}
                                                                        size="sm"
                                                                    >
                                                                        {t(
                                                                            'course.viewFullTopic',
                                                                            'Ver tema completo'
                                                                        )}
                                                                        <ArrowRight
                                                                            size={
                                                                                14
                                                                            }
                                                                        />
                                                                    </SecondaryButton>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Expanded content — grid-rows transition for smooth open/close */}
                                                    <div
                                                        aria-hidden={
                                                            !isExpanded
                                                        }
                                                        className={clsx(
                                                            'ml-4 border-l-2 border-stone-200 pl-3 dark:border-neutral-700',
                                                            'grid transition-all duration-300 ease-in-out',
                                                            isExpanded
                                                                ? 'grid-rows-[1fr]'
                                                                : 'grid-rows-[0fr]'
                                                        )}
                                                    >
                                                        <div className="overflow-hidden">
                                                            <div className="space-y-2 pt-1">
                                                                {topicClasses.map(
                                                                    renderClassRow
                                                                )}
                                                                {topicAssignments.map(
                                                                    renderAssignmentRow
                                                                )}
                                                                {topicEvaluations.map(
                                                                    renderEvaluationRow
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    )}

                                    {/* Section-level items not attached to any topic (kept flat) */}
                                    {orphanAssignments.map(renderAssignmentRow)}
                                    {orphanEvaluations.map(renderEvaluationRow)}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default SectionDrawer;
